<div class="admin-page-wrap">
  <h1 class="page-title"><i class="bi bi-people me-2"></i> Users</h1>
  <p class="page-subtitle">Manage user and admin accounts. Create users, reset passwords, and toggle admin role.</p>

  <!-- User / Admin toggle (blue primary = user-side style) -->
  <div class="mb-3 d-flex align-items-center gap-2">
    <span class="text-muted">Show:</span>
    <div class="btn-group" role="group">
      <button type="button" class="btn" [class.btn-primary]="!showAdmins" [class.btn-outline-primary]="showAdmins" (click)="showAdmins = false; page = 0; loadPage()">
        Users
      </button>
      <button type="button" class="btn" [class.btn-primary]="showAdmins" [class.btn-outline-primary]="!showAdmins" (click)="showAdmins = true; page = 0; loadPage()">
        Admins
      </button>
    </div>
  </div>

  <div class="card">
    <div class="card-body">
      <div class="d-flex justify-content-end mb-3">
        <button type="button" class="btn btn-primary" (click)="openCreate()">Create User</button>
      </div>
      @if (error) {
        <div class="alert alert-danger">{{ error }}</div>
      }
      @if (loading) {
        <p class="text-muted">Loading…</p>
      } @else {
        <div class="table-responsive">
          <table class="table table-hover">
            <thead>
              <tr>
                <th (click)="setSort('username')" class="sortable" role="button" tabindex="0">
                  Username
                  @if (sortBy === 'username') { <i class="bi bi-chevron-{{ sortDir === 'asc' ? 'up' : 'down' }}"></i> }
                </th>
                <th (click)="setSort('firstName')" class="sortable" role="button" tabindex="0">
                  First name
                  @if (sortBy === 'firstName') { <i class="bi bi-chevron-{{ sortDir === 'asc' ? 'up' : 'down' }}"></i> }
                </th>
                <th (click)="setSort('lastName')" class="sortable" role="button" tabindex="0">
                  Last name
                  @if (sortBy === 'lastName') { <i class="bi bi-chevron-{{ sortDir === 'asc' ? 'up' : 'down' }}"></i> }
                </th>
                <th (click)="setSort('country')" class="sortable" role="button" tabindex="0">
                  Country
                  @if (sortBy === 'country') { <i class="bi bi-chevron-{{ sortDir === 'asc' ? 'up' : 'down' }}"></i> }
                </th>
                <th (click)="setSort('authority')" class="sortable" role="button" tabindex="0">
                  Authority
                  @if (sortBy === 'authority') { <i class="bi bi-chevron-{{ sortDir === 'asc' ? 'up' : 'down' }}"></i> }
                </th>
                <th (click)="setSort('createdAt')" class="sortable" role="button" tabindex="0">
                  Created at
                  @if (sortBy === 'createdAt') { <i class="bi bi-chevron-{{ sortDir === 'asc' ? 'up' : 'down' }}"></i> }
                </th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              @for (u of content; track u.id) {
                <tr class="clickable-row" (click)="openDetails(u)" role="button" tabindex="0">
                  <td>{{ u.username }}</td>
                  <td>{{ u.firstName }}</td>
                  <td>{{ u.lastName }}</td>
                  <td>{{ u.country ?? '—' }}</td>
                  <td>{{ u.authority ?? '—' }}</td>
                  <td>{{ u.createdAt }}</td>
                  <td (click)="$event.stopPropagation()">
                    <button type="button" class="btn btn-sm btn-outline-danger" (click)="openDeleteConfirm(u)">Delete</button>
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
        @if (totalPages > 1) {
          <nav class="mt-3">
            <ul class="pagination mb-0">
              <li class="page-item" [class.disabled]="page === 0">
                <a class="page-link" href="javascript:void(0)" (click)="goToPage(page - 1)">Previous</a>
              </li>
              <li class="page-item disabled"><span class="page-link">{{ page + 1 }} / {{ totalPages }}</span></li>
              <li class="page-item" [class.disabled]="page >= totalPages - 1">
                <a class="page-link" href="javascript:void(0)" (click)="goToPage(page + 1)">Next</a>
              </li>
            </ul>
          </nav>
        }
      }
    </div>
  </div>
</div>

<!-- Detail modal -->
@if (detailUser) {
  <div class="modal show d-block" tabindex="-1" style="background: rgba(0,0,0,0.5);">
    <div class="modal-dialog modal-lg modal-dialog-scrollable">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">{{ detailUser.id == null ? 'Create user' : 'User: ' + detailUser.username }}</h5>
          <button type="button" class="btn-close" (click)="closeDetails()"></button>
        </div>
        <div class="modal-body">
          @if (detailMessage) {
            <div class="alert alert-info mb-2">{{ detailMessage }}</div>
          }
          <ul class="nav nav-tabs mb-3">
            <li class="nav-item">
              <button class="nav-link" [class.active]="detailTab === 'details'" (click)="detailTab = 'details'" type="button">Details</button>
            </li>
            <li class="nav-item">
              <button class="nav-link" [class.active]="detailTab === 'subscriptions'" (click)="detailTab = 'subscriptions'" type="button" [disabled]="detailUser.id == null">Subscriptions</button>
            </li>
          </ul>

          @if (detailTab === 'details') {
            @if (detailUser.id == null) {
              <div class="row g-2 mb-2">
                <div class="col-md-6">
                  <label class="form-label">Username <span class="text-danger">*</span></label>
                  <input type="text" class="form-control" [(ngModel)]="detailForm.username" placeholder="Username">
                </div>
                <div class="col-md-6">
                  <label class="form-label">Password <span class="text-danger">*</span></label>
                  <input type="password" class="form-control" [(ngModel)]="detailForm.password" placeholder="Password">
                </div>
              </div>
            }
            <div class="row g-2 mb-2">
              <div class="col-md-6">
                <label class="form-label">First name</label>
                <input type="text" class="form-control" [(ngModel)]="detailForm.firstName">
              </div>
              <div class="col-md-6">
                <label class="form-label">Last name</label>
                <input type="text" class="form-control" [(ngModel)]="detailForm.lastName">
              </div>
            </div>
            <div class="mb-2">
              <label class="form-label">Email</label>
              <input type="email" class="form-control" [(ngModel)]="detailForm.email">
            </div>
            <div class="row g-2 mb-2">
              <div class="col-md-6">
                <label class="form-label">Country</label>
                <select class="form-select" [(ngModel)]="detailForm.countryId" (ngModelChange)="onCountryChange()">
                  <option [ngValue]="null">—</option>
                  @for (c of countries; track c.id) {
                    <option [ngValue]="c.id">{{ c.name }}</option>
                  }
                </select>
              </div>
              <div class="col-md-6">
                <label class="form-label">Authority</label>
                <select class="form-select" [(ngModel)]="detailForm.authorityId">
                  <option [ngValue]="null">—</option>
                  @for (a of filteredAuthorities; track a.id) {
                    <option [ngValue]="a.id">{{ a.countryName }} - {{ a.name }}</option>
                  }
                </select>
              </div>
            </div>
            <div class="form-check mb-2">
              <input type="checkbox" class="form-check-input" id="detailActive" [(ngModel)]="detailForm.active">
              <label class="form-check-label" for="detailActive">Active</label>
            </div>
            <div class="form-check mb-2">
              <input type="checkbox" class="form-check-input" id="detailAdmin" [(ngModel)]="detailForm.admin">
              <label class="form-check-label" for="detailAdmin">Admin</label>
            </div>
            <div class="form-check mb-2">
              <input type="checkbox" class="form-check-input" id="detailMarketing" [(ngModel)]="detailForm.acceptMarketing">
              <label class="form-check-label" for="detailMarketing">Accept marketing</label>
            </div>
          }

          @if (detailTab === 'subscriptions') {
            <div class="mb-3 p-3 border rounded">
              <h6>Assign subscription</h6>
              <div class="row g-2 align-items-end">
                <div class="col-md-4">
                  <label class="form-label">Subscription</label>
                  <select class="form-select" [(ngModel)]="assignSubscriptionId">
                    <option [ngValue]="null">Select…</option>
                    @for (s of availableSubscriptions; track s.id) {
                      <option [ngValue]="s.id">{{ s.name }}</option>
                    }
                  </select>
                </div>
                <div class="col-md-3">
                  <label class="form-label">From</label>
                  <input type="date" class="form-control subscription-date" [(ngModel)]="assignValidFrom">
                </div>
                <div class="col-md-3">
                  <label class="form-label">To</label>
                  <input type="date" class="form-control subscription-date" [(ngModel)]="assignValidTo">
                </div>
                <div class="col-md-2">
                  <button type="button" class="btn btn-primary w-100" [disabled]="!assignSubscriptionId || assignLoading" (click)="assignSubscription()">Assign</button>
                </div>
              </div>
            </div>
            <h6>Assignments</h6>
            <ul class="list-group">
              @for (us of subscriptionsList; track us.id) {
                <li class="list-group-item d-flex justify-content-between align-items-center">
                  <span>{{ us.subscriptionName }} — {{ us.validFrom }} to {{ us.validTo }}</span>
                  <button type="button" class="btn btn-sm btn-outline-danger" (click)="cancelSubscription(us)">Cancel subscription</button>
                </li>
              }
              @if (subscriptionsList.length === 0) {
                <li class="list-group-item text-muted">No subscriptions assigned.</li>
              }
            </ul>
          }
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" (click)="closeDetails()">Close</button>
          <button type="button" class="btn btn-primary" [disabled]="detailSaving" (click)="save()">Save</button>
        </div>
      </div>
    </div>
  </div>
}

<!-- Delete confirm modal -->
@if (deleteUser) {
  <div class="modal show d-block" tabindex="-1" style="background: rgba(0,0,0,0.5);">
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Delete user</h5>
          <button type="button" class="btn-close" (click)="closeDeleteConfirm()"></button>
        </div>
        <div class="modal-body">
          <p>Permanently delete user <strong>{{ deleteUser.username }}</strong>? This cannot be undone.</p>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" (click)="closeDeleteConfirm()">Cancel</button>
          <button type="button" class="btn btn-danger" [disabled]="deleteInProgress" (click)="confirmDelete()">Delete</button>
        </div>
      </div>
    </div>
  </div>
}
